import type { FC, ReactNode } from 'react'
import { createContext, useCallback, useEffect, useReducer } from 'react'
import { authApi } from '@/api/auth'
import { Issuer } from '@/types/auth'
import { Language, LoginParameter, User } from '@/api'
import * as api from '@/api'
import { shouldForgetSession } from './shouldForgetSession'
import {
  maintenanceOf,
  sameMaintenance,
  watchMaintenance,
  type Maintenance,
} from './maintenance'
import {
  ownSessionToKeep,
  readImpersonation,
  sessionAfterEnd,
  type ImpersonationEnd,
} from './impersonation'
import { decodeWithoutBearer } from '@/utils/jwt'
import { createToast } from '@/utils'
import { getApiErrorStatus } from '@/utils/apiError'
import { wait } from '@/utils/wait'
import { tokens } from '@/locales/tokens'
import { useTranslation } from 'react-i18next'

const STORAGE_KEY = 'accessToken'
const STAFF_TOKEN_KEY = 'staffAccessToken'

interface State {
  isInitialized: boolean
  isAuthenticated: boolean
  isImpersonating: boolean
  /** Fin de la session « en tant que », en millisecondes ; null hors impersonation. */
  impersonationExpiresAt: number | null
  user: Partial<User> | null
  /** Coupure planifiée en cours : l'écran de maintenance remplace l'application. */
  maintenance: Maintenance | null
}

enum ActionType {
  INITIALIZE = 'INITIALIZE',
  MAINTENANCE = 'MAINTENANCE',
  SIGN_IN = 'SIGN_IN',
  SIGN_OUT = 'SIGN_OUT',
  UPDATE = 'UPDATE',
}

type InitializeAction = {
  type: ActionType.INITIALIZE
  payload: {
    isAuthenticated: boolean
    isImpersonating: boolean
    impersonationExpiresAt: number | null
    user: Partial<User> | null
  }
}

type MaintenanceAction = {
  type: ActionType.MAINTENANCE
  payload: Maintenance
}

type SignInAction = {
  type: ActionType.SIGN_IN
  payload: {
    user: Partial<User>
    isImpersonating: boolean
    impersonationExpiresAt: number | null
  }
}

type SignOutAction = {
  type: ActionType.SIGN_OUT
}

type UpdateAction = {
  type: ActionType.UPDATE
  payload: {
    user: Partial<User>
  }
}

type Action =
  | InitializeAction
  | MaintenanceAction
  | SignInAction
  | SignOutAction
  | UpdateAction

type Handler = (state: State, action: any) => State

const initialState: State = {
  isAuthenticated: false,
  isInitialized: false,
  isImpersonating: false,
  impersonationExpiresAt: null,
  user: null,
  maintenance: null,
}

const handlers: Record<ActionType, Handler> = {
  INITIALIZE: (state: State, action: InitializeAction): State => {
    const { isAuthenticated, isImpersonating, impersonationExpiresAt, user } =
      action.payload

    return {
      ...state,
      isAuthenticated,
      isImpersonating,
      impersonationExpiresAt,
      isInitialized: true,
      user,
      maintenance: null,
    }
  },
  // Chaque appel d'un écran ouvert reçoit la 503 : la même fin annoncée garde l'état, rien ne se re-rend.
  MAINTENANCE: (state: State, action: MaintenanceAction): State =>
    state.maintenance && sameMaintenance(state.maintenance, action.payload)
      ? state
      : { ...state, maintenance: action.payload },
  SIGN_IN: (state: State, action: SignInAction): State => {
    const { user, isImpersonating, impersonationExpiresAt } = action.payload

    return {
      ...state,
      isAuthenticated: true,
      isImpersonating,
      impersonationExpiresAt,
      user,
    }
  },
  SIGN_OUT: (state: State): State => ({
    ...state,
    isAuthenticated: false,
    isImpersonating: false,
    impersonationExpiresAt: null,
    user: null,
  }),
  UPDATE: (state: State, action: UpdateAction): State => {
    const { user } = action.payload

    return {
      ...state,
      isAuthenticated: true,
      user,
    }
  },
}

const reducer = (state: State, action: Action): State =>
  handlers[action.type] ? handlers[action.type](state, action) : state

export interface AuthContextType extends State {
  issuer: Issuer.JWT
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => void
  renewPassword: (
    newPassword: string,
    tid?: string,
    token?: string
  ) => Promise<void>
  attest: (
    //email: string,
    newPassword: string,
    tid?: string,
    token?: string
  ) => Promise<void>
  lostAccess: (email: string) => Promise<void>
  updateUserContext: (user: Partial<User>) => void
  /** Ouvre la session « en tant que » d'un code passé par la console (TEC-1911). */
  startImpersonation: (code: string) => Promise<void>
  /** Ferme la session « en tant que » ; vrai si sa propre session a repris, faux si déconnecté. */
  stopImpersonation: (reason: ImpersonationEnd) => Promise<boolean>
}

export const AuthContext = createContext<AuthContextType>({
  ...initialState,
  issuer: Issuer.JWT,
  signIn: () => Promise.resolve(),
  signOut: () => {},
  renewPassword: () => Promise.resolve(),
  attest: () => Promise.resolve(),
  lostAccess: () => Promise.resolve(),
  updateUserContext: () => {},
  startImpersonation: () => Promise.resolve(),
  stopImpersonation: () => Promise.resolve(false),
})

interface AuthProviderProps {
  children: ReactNode
}

/**
 * Le temps qu'une gateway redémarre, pas une seconde de plus.
 *
 * Deux essais d'une seconde couvrent le redémarrage qu'on voulait absorber ; quatre essais de
 * deux secondes, c'était huit secondes d'écran d'attente avant de rendre la main — plus pénible
 * que la déconnexion qu'on cherchait à éviter. Et renoncer ne coûte plus la session : le jeton
 * reste, un simple rechargement la retrouve.
 */
const SESSION_RETRIES = 2
const SESSION_RETRY_MS = 1000
/** Pendant une coupure planifiée, l'écran de maintenance réessaie à ce rythme. */
const MAINTENANCE_RETRY_MS = 30_000

/** Langue de l'interface portée par un profil (`fr`, `en`, `de`, ou `fr-FR`…), sinon undefined. */
const interfaceLanguageOf = (language?: string | null): Language | undefined =>
  [Language.Fr, Language.En, Language.De].find(
    (code) => language === code || language?.startsWith(`${code}-`)
  )

/** Une session « en tant que » qui se ferme sans « Terminer » le dit. */
const announceImpersonationEnd = (
  reason: ImpersonationEnd,
  translate: (key: string) => string
): void => {
  if (reason === 'ended') return
  createToast(
    'warning',
    translate(
      reason === 'expired'
        ? tokens.common.impersonationExpired
        : tokens.common.impersonationClosed
    )
  )
}

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props
  const [state, dispatch] = useReducer(reducer, initialState)

  const { i18n } = useTranslation()

  // Avant `initialize` : sa première lecture du membre passe déjà par cette veille.
  useEffect(
    () =>
      watchMaintenance((maintenance) =>
        dispatch({ type: ActionType.MAINTENANCE, payload: maintenance })
      ),
    []
  )

  const initialize = useCallback(async (): Promise<void> => {
    for (let attempt = 0; ; attempt += 1) {
      const accessToken = window.localStorage.getItem(STORAGE_KEY)
      const impersonation = readImpersonation(accessToken)
      try {
        if (accessToken) {
          const decodedToken = decodeWithoutBearer(accessToken)

          api.defaults.headers = {
            ...api.defaults.headers,
            Authorization: `Bearer ${accessToken}`,
          }

          const user = await api.findOneUser(decodedToken.sub)
          const stored = interfaceLanguageOf(localStorage.getItem('language'))
          const profile = interfaceLanguageOf(user.profile?.language)

          // Le profil fait foi (TEC-1922) : la langue gardée dans ce navigateur n'en est
          // que la copie, et celle d'un autre compte ou de la page de connexion ne le
          // réécrit plus. En impersonation, l'équipe Nabu garde la sienne, sans rien écrire.
          const next =
            (impersonation ? stored ?? profile : profile ?? stored) ??
            Language.Fr
          localStorage.setItem('language', next)
          void i18n.changeLanguage(next)

          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated: true,
              isImpersonating: Boolean(impersonation),
              impersonationExpiresAt: impersonation?.expiresAt ?? null,
              user: user,
            },
          })
        } else {
          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated: false,
              isImpersonating: false,
              impersonationExpiresAt: null,
              user: null,
            },
          })
        }
        return
      } catch (err) {
        console.error(err)
        // N'effacer le jeton QUE si le serveur l'a refusé. Sur une gateway indisponible — un
        // redémarrage suffit — le jeton reste valide (RS256, clé de fichier, sans expiration) :
        // l'effacer déconnectait une session vivante pour deux secondes d'indisponibilité.
        if (shouldForgetSession(err)) {
          const ownToken = localStorage.getItem(STAFF_TOKEN_KEY)
          localStorage.removeItem(STAFF_TOKEN_KEY)
          // Session « en tant que » échue ou fermée entre-temps : on retrouve la sienne, s'il y en avait une.
          if (impersonation) {
            const expired =
              impersonation.expiresAt !== null &&
              impersonation.expiresAt <= Date.now()
            announceImpersonationEnd(expired ? 'expired' : 'closed', (key) =>
              i18n.t(key)
            )
            if (ownToken) {
              localStorage.setItem(STORAGE_KEY, ownToken)
              continue
            }
          }
          localStorage.removeItem(STORAGE_KEY)
          break
        }
        // Coupure planifiée : l'écran de maintenance remplace la connexion, la session reste à
        // ouvrir. La relance revient à l'essai périodique plus bas, qui rappelle `initialize`.
        const announced = maintenanceOf(err)
        if (announced) {
          // L'écran passe avant le profil : dans la langue gardée ici, comme la connexion.
          const stored = interfaceLanguageOf(localStorage.getItem('language'))
          if (stored) void i18n.changeLanguage(stored)
          dispatch({ type: ActionType.MAINTENANCE, payload: announced })
          return
        }
        // Garder le jeton ne suffisait pas : on annonçait quand même « non authentifié », donc
        // l'écran de connexion s'affichait et le déclarant se croyait déconnecté. Une panne
        // passagère se RÉESSAIE — ce n'est qu'après avoir vraiment renoncé qu'on le renvoie là.
        if (attempt >= SESSION_RETRIES) break
        await wait(SESSION_RETRY_MS)
      }
    }
    dispatch({
      type: ActionType.INITIALIZE,
      payload: {
        isAuthenticated: false,
        isImpersonating: false,
        impersonationExpiresAt: null,
        user: null,
      },
    })
  }, [dispatch])

  useEffect(
    () => void initialize(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  // Pendant la coupure, un essai toutes les 30 s. Dès que l'API répond autre chose qu'une 5xx ou
  // une panne réseau, `initialize` relit la session comme au chargement (jeton refusé → connexion)
  // et retire l'écran ; s'il reçoit encore la 503 du Worker, l'écran reste et l'essai reprend.
  const inMaintenance = state.maintenance !== null
  useEffect(() => {
    if (!inMaintenance) return
    let stopped = false
    let timer: ReturnType<typeof setTimeout>
    const retry = () => {
      if (!stopped) timer = setTimeout(() => void probe(), MAINTENANCE_RETRY_MS)
    }
    const probe = async (): Promise<void> => {
      try {
        await api.callAccountMe()
      } catch (err) {
        const status = getApiErrorStatus(err)
        if (status === undefined || status >= 500) return retry()
      }
      if (stopped) return
      await initialize()
      retry()
    }
    retry()
    return () => {
      stopped = true
      clearTimeout(timer)
    }
  }, [inMaintenance, initialize])

  const applyAccessToken = useCallback(
    async (accessToken: string): Promise<void> => {
      const decodedToken = decodeWithoutBearer(accessToken)
      const impersonation = readImpersonation(accessToken)
      const authorization = `Bearer ${accessToken}`

      // Le jeton n'est retenu qu'une fois le membre lu : un échec laisse la session d'avant en place.
      const user = await api.findOneUser(decodedToken.sub, {
        headers: { Authorization: authorization },
      })

      localStorage.setItem(STORAGE_KEY, accessToken)
      api.defaults.headers = {
        ...api.defaults.headers,
        Authorization: authorization,
      }
      const session = {
        isImpersonating: Boolean(impersonation),
        impersonationExpiresAt: impersonation?.expiresAt ?? null,
      }

      // La langue du profil l'emporte dès la connexion (TEC-1922) : celle de la page de
      // connexion vient du navigateur ou du dernier compte connecté ici, pas du membre.
      // En impersonation, l'équipe Nabu garde la sienne.
      const profile = interfaceLanguageOf(user.profile?.language)
      const shown = !impersonation && profile ? profile : i18n.language
      void i18n.changeLanguage(shown)
      localStorage.setItem('language', shown)

      dispatch({
        type: ActionType.SIGN_IN,
        payload: {
          user:
            shown === user.profile?.language
              ? user
              : {
                  ...user,
                  profile: { ...user.profile, language: shown as Language },
                },
          ...session,
        },
      })
    },
    [dispatch, i18n]
  )

  const signIn = useCallback(
    async (email: string, password: string): Promise<void> => {
      const { accessToken } = await authApi.signIn({ email, password })
      localStorage.removeItem(STAFF_TOKEN_KEY)
      await applyAccessToken(accessToken)
    },
    [applyAccessToken]
  )

  const renewPassword = useCallback(
    async (
      renewPassword: string,
      tid?: string | undefined,
      token?: string
    ): Promise<void> => {
      const login: LoginParameter = {
        password: renewPassword,
      }
      await authApi.renewPassword({
        login,
        previousPassword: null,
        tid,
        token,
      })
    },
    [dispatch]
  )

  const attest = useCallback(
    async (
      //email: string,
      renewPassword: string,
      tid?: string,
      token?: string
    ): Promise<void> => {
      const login: LoginParameter = {
        //email: email,
        password: renewPassword,
      }
      await authApi.attest({
        login,
        previousPassword: null,
        tid,
        token,
      })
    },
    [dispatch]
  )

  const signOut = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(STAFF_TOKEN_KEY)
    dispatch({ type: ActionType.SIGN_OUT })
  }, [dispatch])

  const startImpersonation = useCallback(
    async (code: string): Promise<void> => {
      const { accessToken } = await authApi.exchangeImpersonationCode(code)
      const ownToken = ownSessionToKeep(
        localStorage.getItem(STORAGE_KEY),
        localStorage.getItem(STAFF_TOKEN_KEY)
      )

      await applyAccessToken(accessToken)
      if (ownToken) {
        localStorage.setItem(STAFF_TOKEN_KEY, ownToken)
      } else {
        localStorage.removeItem(STAFF_TOKEN_KEY)
      }
    },
    [applyAccessToken]
  )

  const stopImpersonation = useCallback(
    async (reason: ImpersonationEnd): Promise<boolean> => {
      // Le jeton de cet onglet : un autre onglet a pu changer celui du stockage entre-temps.
      const authorization = api.defaults.headers?.Authorization
      const ours =
        typeof authorization === 'string'
          ? authorization.replace(/^Bearer /, '')
          : null
      if (reason === 'ended') {
        // Sans réponse de la gateway, le jeton, oublié ici, échoit de lui-même dans l'heure.
        await authApi.endImpersonation().catch((err) => console.error(err))
      }
      announceImpersonationEnd(reason, (key) => i18n.t(key))

      const next = sessionAfterEnd(
        ours,
        localStorage.getItem(STORAGE_KEY),
        localStorage.getItem(STAFF_TOKEN_KEY)
      )
      // La session gardée ne sert qu'à une session « en tant que » : celle qu'on suit peut en avoir besoin.
      if (!readImpersonation(next)) localStorage.removeItem(STAFF_TOKEN_KEY)
      if (next) {
        try {
          await applyAccessToken(next)
          return true
        } catch (err) {
          console.error(err)
          // Coupure planifiée : la session gardée sera rouverte à la fin, sous l'écran de maintenance.
          if (maintenanceOf(err)) {
            localStorage.setItem(STORAGE_KEY, next)
            return true
          }
        }
      }
      signOut()
      return false
    },
    [applyAccessToken, i18n, signOut]
  )

  const lostAccess = useCallback(
    async (email: string): Promise<void> => {
      await authApi.lostAccess({ email })
    },
    [dispatch]
  )

  const updateUserContext = useCallback(
    (user: Partial<User>) => {
      dispatch({
        type: ActionType.UPDATE,
        payload: {
          user,
        },
      })
    },
    [dispatch]
  )

  return (
    <AuthContext.Provider
      value={{
        ...state,
        issuer: Issuer.JWT,
        signIn,
        signOut,
        renewPassword,
        attest,
        lostAccess,
        updateUserContext,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const AuthConsumer = AuthContext.Consumer
